import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { ExternalLink, Sparkles, ArrowLeft } from 'lucide-react';
import { feedService } from '@/core/api';
import { useTelegram, useStore } from '@/core/providers';
import { LoadingSpinner } from '@/components';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import type { FeedItem, FeedResponse, SummaryResponse } from '@/types';

const stripSourceFromTitle = (title: string) => {
  if (!title) return '';
  const separators = [' - ', ' | ', ' — ', ' – '];
  for (const separator of separators) {
    if (title.includes(separator)) {
      const parts = title.split(separator);
      if (parts.length > 1) {
        const cleaned = parts.slice(0, -1).join(separator).trim();
        // If cleaned text is reasonably long, treat last chunk as source name
        if (cleaned.length > 30) return cleaned;
      }
    }
  }
  return title.trim();
};

const cleanContent = (content?: string | null) => {
  if (!content) return '';
  return content
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

function FeedDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showBackButton, hideBackButton, hapticFeedback } = useTelegram();
  const { settings } = useStore();
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  const locationItem = (location.state as { item?: FeedItem } | null)?.item;

  const cachedGlobal = queryClient.getQueryData<InfiniteData<FeedResponse>>([
    'feed',
    'global',
    settings.language,
  ]);

  const cachedPersonal = queryClient.getQueriesData<InfiniteData<FeedResponse>>({
    queryKey: ['feed', 'personal'],
  });

  const cachedItem = useMemo(() => {
    if (!id) return null;

    const searchPages = (pages?: FeedResponse[]) =>
      pages?.flatMap((page) => page.items).find((feedItem) => feedItem.id === id);

    const fromGlobal = cachedGlobal ? searchPages(cachedGlobal.pages) : undefined;
    if (fromGlobal) return fromGlobal;

    for (const [, data] of cachedPersonal) {
      const match = data ? searchPages(data.pages) : undefined;
      if (match) return match;
    }
    return null;
  }, [id, cachedGlobal, cachedPersonal]);

  const item = locationItem ?? cachedItem;

  useEffect(() => {
    showBackButton(() => {
      navigate(-1);
    });
    return () => hideBackButton();
  }, [showBackButton, hideBackButton, navigate]);

  const handleSummarize = async (options?: { silent?: boolean }) => {
    if (!item?.link) return;
    setIsLoadingSummary(true);
    if (!options?.silent) {
      hapticFeedback('impact');
    }
    try {
      const result = await feedService.summarize(
        item.link,
        settings.language,
        item.title
      );
      setSummary(result);
    } catch (error) {
      console.error('Failed to summarize:', error);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  useEffect(() => {
    if (!summary && item?.link && !isLoadingSummary) {
      void handleSummarize({ silent: true });
    }
  }, [summary, item?.link, isLoadingSummary]);

  const handleOpenLink = () => {
    if (item?.link) {
      hapticFeedback('selection');
      window.open(item.link, '_blank');
    }
  };

  const dateLocale = settings.language === 'zh' ? zhCN : enUS;

  if (!item) {
    return (
      <div className="p-6 text-center text-telegram-hint space-y-2">
        <p className="font-medium text-telegram-text">Article unavailable</p>
        <p className="text-sm">
          Please reopen this story from the feed so we can load its details locally.
        </p>
      </div>
    );
  }

  const translatedTitle =
    settings.language === 'zh' && item.title_zh ? item.title_zh : item.title;
  const translatedSummary =
    settings.language === 'zh' && item.summary_zh ? item.summary_zh : item.summary;

  const displayTitle =
    summary?.title_line || item.ai_title || translatedTitle;
  const cleanedDisplayTitle = useMemo(
    () => stripSourceFromTitle(displayTitle),
    [displayTitle]
  );
  const displaySummary =
    summary?.summary || item.ai_summary || translatedSummary;
  const displayKeyPoints = summary?.key_points || item.ai_key_points;
  const originalContent = summary?.original_content_snippet;
  const translatedContent = summary?.translated_content;
  const articleContent = translatedContent || originalContent;
  const cleanedArticleContent = cleanContent(articleContent);
  const cleanedOriginalContent = cleanContent(originalContent);
  const showOriginalSnippet =
    translatedContent && originalContent && translatedContent !== originalContent;

  return (
    <div className="pb-4">
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
          >
            <ArrowLeft size={18} />
          </button>
        </div>
      </div>

      {item.image_url && (
        <div className="aspect-video w-full bg-telegram-secondary">
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="px-4 pt-20 space-y-4">
        <div
          className="rounded-2xl p-4 shadow-lg text-[var(--ff-hero-text)]"
          style={{ background: 'var(--ff-hero-gradient)' }}
        >
          <div className="flex items-center justify-between text-[11px] text-[var(--ff-hero-muted)] mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {item.category && (
                <span className="rounded-full bg-[var(--ff-hero-chip)] px-3 py-1">
                  {item.category}
                </span>
              )}
              <button
                onClick={handleOpenLink}
                className="rounded-full bg-[var(--ff-hero-chip)] px-3 py-1 border border-[var(--ff-hero-chip-border)] hover:bg-[var(--ff-hero-chip)] transition text-[var(--ff-hero-text)]"
              >
                {item.sources?.length ?? 1} 来源
              </button>
              <span className="rounded-full bg-[var(--ff-hero-chip-subtle)] px-3 py-1">
                {formatDistanceToNow(new Date(item.published_at), {
                  addSuffix: true,
                  locale: dateLocale,
                })}
              </span>
            </div>
            <button
              onClick={() => void handleSummarize()}
              disabled={isLoadingSummary}
              className="rounded-full bg-[var(--ff-hero-chip)] px-3 py-1 text-[var(--ff-hero-text)] hover:bg-[var(--ff-hero-chip)] transition"
            >
              {isLoadingSummary ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  分析中
                </span>
              ) : (
                '生成/刷新'
              )}
            </button>
          </div>
          <h2 className="text-2xl font-semibold leading-snug mb-2">
            {cleanedDisplayTitle}
          </h2>
          {displaySummary && (
            <p className="text-sm text-[var(--ff-hero-muted)] leading-relaxed">
              {displaySummary}
            </p>
          )}
        </div>

        <div className="rounded-xl bg-[var(--ff-surface)] border border-[var(--ff-border)] shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-telegram-text">
            <Sparkles size={16} className="text-telegram-button" />
            <span>AI 总结</span>
          </div>
          {displaySummary ? (
            <p className="text-sm text-telegram-text leading-relaxed">
              {displaySummary}
            </p>
          ) : (
            <button
              onClick={() => void handleSummarize()}
              disabled={isLoadingSummary}
              className="btn-secondary w-full justify-center"
            >
              {isLoadingSummary ? (
                <LoadingSpinner size="sm" />
              ) : (
                '点击生成 AI 总结'
              )}
            </button>
          )}
          {displayKeyPoints && displayKeyPoints.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-telegram-hint">
                核心要素
              </p>
              <ul className="space-y-2">
                {displayKeyPoints.map((point, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-telegram-text"
                  >
                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-telegram-button" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {(cleanedArticleContent || isLoadingSummary) && (
          <div className="rounded-xl bg-[var(--ff-surface-alt)] border border-[var(--ff-border)] p-4 space-y-3">
            <div className="flex items-center justify-between text-xs font-semibold text-telegram-hint uppercase tracking-wide">
              <span>原文内容</span>
              {showOriginalSnippet && <span className="text-[10px]">已自动翻译</span>}
            </div>
            {isLoadingSummary && !articleContent && (
              <div className="flex items-center gap-2 text-telegram-text text-sm">
                <LoadingSpinner size="sm" />
                <span>正在提取原文...</span>
              </div>
            )}
            {cleanedArticleContent && (
              <p className="text-sm text-telegram-text leading-relaxed whitespace-pre-wrap">
                {cleanedArticleContent}
              </p>
            )}
            {showOriginalSnippet && cleanedOriginalContent && (
              <div className="rounded-lg bg-[var(--ff-surface)] border border-[var(--ff-border)] p-3 space-y-1">
                <div className="text-[11px] font-semibold text-telegram-hint uppercase tracking-wide">
                  原文摘录
                </div>
                <p className="text-sm text-telegram-text leading-relaxed whitespace-pre-wrap">
                  {cleanedOriginalContent}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleOpenLink}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            <ExternalLink size={16} />
            <span>阅读原文</span>
          </button>
        </div>

        {item.sources && item.sources.length > 0 && (
          <div className="pt-2 space-y-2">
            <span className="text-xs text-telegram-hint uppercase tracking-wide">
              Sources
            </span>
            <ul className="space-y-1 text-sm text-telegram-text">
              {item.sources.map((source, index) => (
                <li key={source.link ?? `${source.source}-${index}`}>
                  {source.link ? (
                    <a
                      href={source.link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-telegram-button hover:underline"
                    >
                      {source.title || source.source}
                    </a>
                  ) : (
                    source.title || source.source
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default FeedDetailPage;
